import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { extractApiErrorMessage } from "../utils/apiErrorUtils";
import i18n from "../i18n";

const runtimeApiUrl = window.env?.REACT_APP_API_URL;
const baseURL = runtimeApiUrl || process.env.REACT_APP_API_URL || "";

const axiosInstance = axios.create(
  baseURL.length === 0
    ? { baseURL: "" }
    : {
        baseURL,
      }
);

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken") || "";
  if (token.length > 0) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  return config;
});

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken) {
        try {
          const { data } = await axios.post(
            `${baseURL}/auth/CreateAccessTokenWithRefreshToken`,
            refreshToken,
            {
              headers: {
                Accept: "*/*",
                "Content-Type": "application/json-patch+json",
              },
            }
          );

          localStorage.setItem("authToken", data.accessToken.token);
          localStorage.setItem("refreshToken", `"${data.refreshToken.token}"`);

          axiosInstance.defaults.headers.common[
            "Authorization"
          ] = `Bearer ${data.accessToken.token}`;
          originalRequest.headers[
            "Authorization"
          ] = `Bearer ${data.accessToken.token}`;

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem("authToken");
          localStorage.removeItem("refreshToken");
          window.location.href = "/giris";
          return Promise.reject(refreshError);
        }
      }

      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/giris";
      return;
    }

    try {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 429) {
        const rateLimitMessage = data?.Message || data?.message || i18n.t("errors.rateLimit");
        toast.error(rateLimitMessage, { autoClose: 2000, position: "top-center" });
        return Promise.reject(error);
      }

      if (error.code === "ECONNABORTED" && originalRequest?.url?.includes("CourseRegisters/Add")) {
        toast.error(i18n.t("errors.registerTimeout"), {
          autoClose: 4000,
          position: "top-center",
        });
        return Promise.reject(error);
      }

      if (!error?.response) {
        const networkMessage = error.code === "ECONNABORTED"
          ? i18n.t("errors.requestTimeout")
          : i18n.t("errors.network");
        toast.error(networkMessage, { autoClose: 5000, position: "top-center" });
        return Promise.reject(error);
      }

      let displayMessage = extractApiErrorMessage(data);

      const finalMessage = displayMessage === "You are not authorized."
        ? i18n.t("errors.unauthorized")
        : (displayMessage || i18n.t("errors.generic"));

      toast.error(typeof finalMessage === "object" ? i18n.t("errors.objectFallback") : finalMessage, {
        autoClose: 5000,
        position: "top-center",
      });

    } catch (err) {
      toast.error(i18n.t("errors.fallback"), { autoClose: 1500 });
    }
    return Promise.reject(error);
  }
);

export { axiosInstance, baseURL };
